import React from 'react';
import { Button, ButtonProps } from '@mui/material';

export interface ActionButtonConfig {
  text: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  fullWidth?: boolean;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  sx?: ButtonProps['sx'];
}

export interface ActionButtonProps extends Omit<ActionButtonConfig, 'onClick'> {
  onClick?: () => void;
  config?: ActionButtonConfig;
}

/**
 * ActionButton - Reusable button component with consistent styling
 * 
 * Can be configured either through props or a config object.
 * Matches the app's standard button design (New Song button style).
 * 
 * @example
 * // Using props
 * <ActionButton 
 *   text="New Song" 
 *   icon={<AddIcon />} 
 *   onClick={handleCreate}
 * />
 * 
 * @example
 * // Using config object
 * <ActionButton config={{
 *   text: "Try again",
 *   icon: <RefreshIcon />,
 *   onClick: handleRetry
 * }} />
 */
const ActionButton: React.FC<ActionButtonProps> = ({ 
  text,
  onClick,
  icon,
  variant = 'contained',
  size = 'medium',
  disabled = false,
  fullWidth = false,
  color = 'primary',
  sx,
  config
}) => {
  // If config is provided, use it to override individual props
  const finalConfig = config || {
    text,
    onClick,
    icon,
    variant,
    size,
    disabled,
    fullWidth,
    color,
    sx
  };

  // Size mapping
  const sizeStyles = {
    small: {
      px: 2,
      py: 0.75,
      fontSize: '13px',
    },
    medium: {
      px: 2.5,
      py: 1,
      fontSize: '14px',
    },
    large: {
      px: 3,
      py: 1.25,
      fontSize: '15px',
    }
  };

  return (
    <Button
      variant={finalConfig.variant}
      onClick={finalConfig.onClick}
      disabled={finalConfig.disabled}
      fullWidth={finalConfig.fullWidth}
      color={finalConfig.color}
      startIcon={finalConfig.icon}
      sx={{
        borderRadius: 2,
        ...sizeStyles[finalConfig.size || 'medium'],
        textTransform: 'none',
        fontWeight: 500,
        ...finalConfig.sx
      }}
    >
      {finalConfig.text}
    </Button>
  );
};

export default ActionButton;
